import { API_URL } from './api';
import type { ConversationSnapshot } from './Conversation';

const SYNC_TOKEN_KEY = 'DirtyLingoSyncToken';
const SYNC_PHONE_KEY = 'DirtyLingoSyncPhone';
const SYNC_USER_KEY = 'DirtyLingoSyncUserId';
const SYNC_EXPIRES_KEY = 'DirtyLingoSyncExpiresAt';

export type SyncSession = {
  token: string;
  userId: string;
  phone: string;
  expiresAt?: string;
};

export function getSyncSession(): SyncSession | null {
  const token = localStorage.getItem(SYNC_TOKEN_KEY);
  const userId = localStorage.getItem(SYNC_USER_KEY);
  const phone = localStorage.getItem(SYNC_PHONE_KEY);
  if (!token || !userId || !phone) {
    return null;
  }
  const expiresAt = localStorage.getItem(SYNC_EXPIRES_KEY) ?? undefined;
  return { token, userId, phone, expiresAt };
}

export function setSyncSession(session: SyncSession) {
  localStorage.setItem(SYNC_TOKEN_KEY, session.token);
  localStorage.setItem(SYNC_USER_KEY, session.userId);
  localStorage.setItem(SYNC_PHONE_KEY, session.phone);
  if (session.expiresAt) {
    localStorage.setItem(SYNC_EXPIRES_KEY, session.expiresAt);
  }
}

export function clearSyncSession() {
  localStorage.removeItem(SYNC_TOKEN_KEY);
  localStorage.removeItem(SYNC_USER_KEY);
  localStorage.removeItem(SYNC_PHONE_KEY);
  localStorage.removeItem(SYNC_EXPIRES_KEY);
}

export function isSyncEnabled() {
  return !!getSyncSession();
}

export async function startSync(phone: string) {
  if (!API_URL) {
    throw new Error('Missing API_URL');
  }
  const normalized = normalizePhone(phone);
  const response = await fetch(`${API_URL}/auth/sms/start`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ phone: normalized }),
  });
  if (!response.ok) {
    let message = 'Failed to send code';
    try {
      const payload = await response.json();
      if (payload && typeof payload.message === 'string') {
        message = payload.message;
      }
    } catch {
      // Fall back to default message.
    }
    throw new Error(message);
  }
  localStorage.setItem(SYNC_PHONE_KEY, normalized);
}

export async function verifySync(phone: string, code: string): Promise<SyncSession> {
  if (!API_URL) {
    throw new Error('Missing API_URL');
  }
  const trimmedPhone = normalizePhone(phone);
  const trimmedCode = code.trim();
  if (!trimmedCode) {
    throw new Error('Phone and code are required');
  }
  const response = await fetch(`${API_URL}/auth/sms/verify`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ phone: trimmedPhone, code: trimmedCode }),
  });
  if (!response.ok) {
    throw new Error('Failed to verify code');
  }
  const payload = (await response.json()) as { token: string; userId: string; expiresAt?: string };
  const session: SyncSession = {
    token: payload.token,
    userId: payload.userId,
    expiresAt: payload.expiresAt,
    phone: trimmedPhone,
  };
  setSyncSession(session);
  return session;
}

function normalizePhone(phone: string): string {
  const trimmed = phone.trim();
  if (!trimmed) {
    throw new Error('Phone number required');
  }
  const normalized = trimmed.replace(/[^\d+]/g, '');
  if (!/^\+\d{10,15}$/.test(normalized)) {
    throw new Error('Use +15555551234 format');
  }
  return normalized;
}

export async function pullSnapshot(version: number): Promise<ConversationSnapshot | null> {
  const session = getSyncSession();
  if (!session || !API_URL) {
    return null;
  }
  const response = await fetch(`${API_URL}/sync/pull`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${session.token}`,
      'x-client-version': String(version),
    },
  });
  if (response.status === 409) {
    return Promise.reject(new Error('sync_version_too_old'));
  }
  if (response.status === 401) {
    clearSyncSession();
    return Promise.reject(new Error('sync_unauthorized'));
  }
  if (!response.ok) {
    return null;
  }
  const payload = (await response.json()) as { snapshot?: ConversationSnapshot | null; token?: string };
  if (payload.token) {
    setSyncSession({ ...session, token: payload.token });
  }
  return payload.snapshot ?? null;
}

export async function pushSnapshot(snapshot: ConversationSnapshot, options?: { keepalive?: boolean }) {
  const session = getSyncSession();
  if (!session || !API_URL) {
    return;
  }
  const response = await fetch(`${API_URL}/sync/push`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${session.token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ snapshot }),
    keepalive: options?.keepalive ?? false,
  });
  if (response.status === 409) {
    return Promise.reject(new Error('sync_version_too_old'));
  }
  if (response.status === 401) {
    clearSyncSession();
    return Promise.reject(new Error('sync_unauthorized'));
  }
  if (response.ok) {
    const payload = (await response.json()) as { token?: string };
    if (payload.token) {
      setSyncSession({ ...session, token: payload.token });
    }
  }
}
